import { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentUser, subscribeToAuth } from '../lib/auth';
const KEYS = {
  bookPopups:       'reanlo_guest_book_popups',
  matchmakerSwipes: 'reanlo_guest_matchmaker_swipes',
  blocksBuilt:      'reanlo_guest_blocks_built',
  cravingsSubmitted:'reanlo_guest_cravings_submitted',
};

// (popups/3) + (swipes/2) + (cravings/3) >= 1.0 fires gate
//
// Ticket 21: a submitted craving now counts too -- it didn't before, which
// meant the homepage's actual front door (per the ticket) spent nothing.
// Weighted at /3, the same as a popup, not /2 like a swipe: a guest's
// first craving submission is one deliberate action, same shape as opening
// a book's detail popup, and the results it returns are what then rack up
// real swipe budget as the guest works through them. Weighting it at /2
// (or heavier) double-counts the same engagement almost immediately --
// one craving plus a single swipe on its own results would already hit
// budget, gating a guest before they've seen what the feature can do.
// This number is a starting product call, not a measured one (same
// caveat the research doc gives its own latency numbers) -- worth
// revisiting once there's real usage to look at, the same way
// FREE_INTERACTIONS' /1.5 weighting in ReaderHomepage.jsx was already
// corrected once after it fired a step early.
const HOMEPAGE_BUDGET  = 1.0;
const PROFILE_LIMIT    = 3;
const COMMUNITY_MS     = 10_000;

function get(key)      { try { const v = localStorage.getItem(key); return v === null ? 0 : JSON.parse(v); } catch { return 0; } }
function lsSet(key, v) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* quota */ } }
function clearCounts() { Object.values(KEYS).forEach(k => localStorage.removeItem(k)); }
function budget(p, s, c) { return (p / 3) + (s / 2) + (c / 3); }

// `surface` is the page this gate lives on: 'homepage', 'profile' or
// 'community'. It picks SignUpGate's wording whenever requireAuth opens the
// gate. requireAuth used to hardcode 'community', so a signed-out reader
// pressing Save on the homepage deck was asked to "join the conversation".
export function useGuestGate(surface = 'community') {
  // null = auth resolving | false = confirmed guest | true = confirmed logged-in
  const [isAuthed, setIsAuthed]     = useState(null);
  const [gateVisible, setGateVisible] = useState(false);
  const [gateContext, setGateContext] = useState(null);
  const timerRef       = useRef(null);
  const pendingAction  = useRef(null);

  useEffect(() => {
    // Resolve immediately on mount
    getCurrentUser().then((user) => setIsAuthed(!!user));

    // Shared listener rather than a second registration — see lib/auth.js.
    const unsubscribe = subscribeToAuth((_, session) => {
      const authed = !!session?.user;
      setIsAuthed(authed);
      if (authed) {
        setGateVisible(false);
        clearCounts();
        pendingAction.current?.();
        pendingAction.current = null;
      }
    });
    return () => unsubscribe();
  }, []);

  // ─── Only act when we have confirmed the user is a guest ─────────────────
  // isAuthed === null means Supabase hasn't responded yet — never track then.
  const isGuest = isAuthed === false;

  const openGate = useCallback((ctx) => {
    if (!isGuest) return;
    setGateContext(ctx);
    setGateVisible(true);
  }, [isGuest]);

  // ── Homepage trackers ─────────────────────────────────────────────────────
  const trackBookPopup = useCallback(() => {
    if (!isGuest) return;
    const popups = get(KEYS.bookPopups) + 1;
    lsSet(KEYS.bookPopups, popups);
    if (budget(popups, get(KEYS.matchmakerSwipes), get(KEYS.cravingsSubmitted)) >= HOMEPAGE_BUDGET) openGate('homepage');
  }, [isGuest, openGate]);

  const trackMatchmakerSwipe = useCallback(() => {
    if (!isGuest) return;
    const swipes = get(KEYS.matchmakerSwipes) + 1;
    lsSet(KEYS.matchmakerSwipes, swipes);
    if (budget(get(KEYS.bookPopups), swipes, get(KEYS.cravingsSubmitted)) >= HOMEPAGE_BUDGET) openGate('homepage');
  }, [isGuest, openGate]);

  // Ticket 21 -- submitting a craving is the front door now, so it has to
  // spend budget the same way opening a book popup or swiping the deck
  // already do. Fires on submission itself (a confirmed transcript the
  // reader chose to search with), not on whether embedCraving() came back
  // 'ok' -- a guest who used the feature and hit a timeout still used it;
  // gating that count on a successful embed would let a slow network give
  // guests unlimited free tries.
  const trackCravingSubmit = useCallback(() => {
    if (!isGuest) return;
    const cravings = get(KEYS.cravingsSubmitted) + 1;
    lsSet(KEYS.cravingsSubmitted, cravings);
    if (budget(get(KEYS.bookPopups), get(KEYS.matchmakerSwipes), cravings) >= HOMEPAGE_BUDGET) openGate('homepage');
  }, [isGuest, openGate]);

  // ── Profile tracker ───────────────────────────────────────────────────────
  const trackBlockBuilt = useCallback(() => {
    if (!isGuest) return;
    const n = get(KEYS.blocksBuilt) + 1;
    lsSet(KEYS.blocksBuilt, n);
    if (n >= PROFILE_LIMIT) openGate('profile');
  }, [isGuest, openGate]);

  // ── Community: scroll timer ───────────────────────────────────────────────
  const startCommunityTimer = useCallback(() => {
    if (!isGuest) return () => {};
    timerRef.current = setTimeout(() => openGate('community'), COMMUNITY_MS);
    return () => clearTimeout(timerRef.current);
  }, [isGuest, openGate]);

  // ── Any surface: sign in first, then carry on with what they tapped ──────
  const requireAuth = useCallback((onSuccess) => {
    if (isAuthed === true)  { onSuccess?.(); return; }
    if (isAuthed === false) { pendingAction.current = onSuccess ?? null; openGate(surface); }
    // null = still loading, do nothing
  }, [isAuthed, openGate, surface]);

  return { gateVisible, gateContext, isAuthed, trackBookPopup, trackMatchmakerSwipe, trackCravingSubmit, trackBlockBuilt, startCommunityTimer, requireAuth };
}